import { z } from 'zod';

// =============================================================================
// Enums (mirror Prisma enums in apps/api)
// =============================================================================

export const PlanCategoriaEnum = z.enum([
  'SEGURIDAD_SALUD',
  'MEDIO_AMBIENTE',
  'OPERACIONAL',
  'CPHS',
  'OTRO',
]);
export type PlanCategoria = z.infer<typeof PlanCategoriaEnum>;

export const PlanStatusEnum = z.enum(['BORRADOR', 'PUBLICADO', 'ARCHIVADO']);
export type PlanStatus = z.infer<typeof PlanStatusEnum>;

export const PlanPeriodTypeEnum = z.enum([
  'MESES',
  'SEMANAS',
  'DIAS',
  'PERSONALIZADO',
]);
export type PlanPeriodType = z.infer<typeof PlanPeriodTypeEnum>;

export const PlanEvidenciaTypeEnum = z.enum([
  'FORMULARIO',
  'FOTO',
  'VIDEO',
  'AUDIO',
  'DOCUMENTO',
  'OTRO',
]);
export type PlanEvidenciaType = z.infer<typeof PlanEvidenciaTypeEnum>;

/**
 * Frecuencia de ejecución de una actividad (leaf node). Cliente-side string;
 * el backend solo persiste como texto libre, no es enum DB.
 */
export const PlanFrecuenciaEnum = z.enum([
  'MENSUAL',
  'BIMESTRAL',
  'TRIMESTRAL',
  'CUATRIMESTRAL',
  'SEMESTRAL',
  'ANUAL',
  'CADA_VEZ',
  'UNICA',
  'N_A',
  'OTRO',
]);
export type PlanFrecuencia = z.infer<typeof PlanFrecuenciaEnum>;

/**
 * Status computado client-side a partir de meses[mesIndex] + evidencia presence.
 * NO se persiste en DB — siempre se calcula al render.
 */
export const PlanActivityStatusEnum = z.enum([
  'PENDIENTE',
  'EJECUTADO',
  'NO_APLICA',
]);
export type PlanActivityStatus = z.infer<typeof PlanActivityStatusEnum>;

// =============================================================================
// Column subdivisions (Schwager parity — commit b6e8004a)
// =============================================================================

export const SubColTypeEnum = z.enum(['text', 'date', 'number']);
export type SubColType = z.infer<typeof SubColTypeEnum>;

export const SubColSchema = z.object({
  key: z.string().min(1).max(32),
  label: z.string().min(1).max(50),
  type: SubColTypeEnum,
});
export type SubCol = z.infer<typeof SubColSchema>;

export const ColumnConfigEntrySchema = z.object({
  label: z.string().min(1).max(50),
  subdivided: z.boolean(),
  subcols: z.array(SubColSchema).max(10),
});
export type ColumnConfigEntry = z.infer<typeof ColumnConfigEntrySchema>;

export const PlanColumnConfigSchema = z.array(ColumnConfigEntrySchema).min(1).max(10);
export type PlanColumnConfig = z.infer<typeof PlanColumnConfigSchema>;

/**
 * Returns a fully-populated columnConfig for any plan. When the plan has none
 * yet (legacy plans created before this feature), reconstruct from
 * columnLabels with subdivided=false. Always returns at least one entry.
 */
export function resolveColumnConfig(plan: {
  columnLabels: string[] | null;
  columnConfig: PlanColumnConfig | null;
}): PlanColumnConfig {
  if (plan.columnConfig && plan.columnConfig.length > 0) return plan.columnConfig;
  const labels =
    plan.columnLabels && plan.columnLabels.length > 0
      ? plan.columnLabels
      : ['Elemento', 'Foco', 'Objetivo', 'Actividad'];
  return labels.map((label) => ({ label, subdivided: false, subcols: [] }));
}

/** True when at least one hierarchy column is subdivided. */
export function hasSubdividedColumns(config: PlanColumnConfig): boolean {
  return config.some((c) => c.subdivided && c.subcols.length > 0);
}

/** Number of visual columns a hierarchy column occupies. */
export function columnSpan(entry: ColumnConfigEntry): number {
  return entry.subdivided && entry.subcols.length > 0 ? entry.subcols.length : 1;
}

/** Generate a fresh stable key for a new SubCol. */
export function newSubColKey(): string {
  return `sc_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

// =============================================================================
// Plan CRUD schemas
// =============================================================================

export const PlanCreateSchema = z.object({
  codigo: z.string().min(1).max(50),
  nombre: z.string().min(1).max(200),
  categoria: PlanCategoriaEnum,
  version: z.string().max(20).optional(),
  contrato: z.string().max(100).optional(),
  objetivoGeneral: z.string().max(5000).optional(),
  alcance: z.string().max(5000).optional(),
  keywords: z.array(z.string()).optional(),
  anio: z.number().int().min(2000).max(2100),
  mesInicio: z.number().int().min(1).max(12).optional(),
  periodType: PlanPeriodTypeEnum.optional(),
  numPeriods: z.number().int().min(1).max(53).optional(),
  mesesLabels: z.array(z.string()).optional(),
  customDates: z.array(z.string()).optional(),
  columnLabels: z.array(z.string()).optional(),
  columnWidths: z.array(z.number()).optional(),
  columnConfig: PlanColumnConfigSchema.optional(),
  campos: z.record(z.string(), z.boolean()).optional(),
  tabLabels: z.array(z.string().min(1).max(50)).min(1).optional(),
});
export type PlanCreateInput = z.infer<typeof PlanCreateSchema>;

export const PlanUpdateSchema = PlanCreateSchema.partial().extend({
  status: PlanStatusEnum.optional(),
});
export type PlanUpdateInput = z.infer<typeof PlanUpdateSchema>;

export const PlanTabsUpdateSchema = z.object({
  tabLabels: z.array(z.string().min(1).max(50)).min(1),
});
export type PlanTabsUpdateInput = z.infer<typeof PlanTabsUpdateSchema>;

export const PlanListFilterSchema = z.object({
  status: PlanStatusEnum.optional(),
  categoria: PlanCategoriaEnum.optional(),
  anio: z.coerce.number().int().min(2000).max(2100).optional(),
});
export type PlanListFilter = z.infer<typeof PlanListFilterSchema>;

// =============================================================================
// PlanNode schemas
// =============================================================================

const MesesArraySchema = z
  .array(z.boolean())
  .max(53, 'meses array exceeds max periods (53 weeks)');

/** Per-node values for subdivided sub-columns. { [subcolKey]: stringValue }. */
const NodeAttrsSchema = z.record(z.string().max(32), z.string().max(500));

export const PlanNodeCreateSchema = z.object({
  parentId: z.number().int().positive().nullable().optional(),
  nombre: z.string().min(1).max(200),
  orden: z.number().int().min(0).optional(),
  tabIndex: z.number().int().min(0).optional(),
  /** Leaf-only metadata. If parentId is set and the parent had metadata, the
   *  service atomically clears the parent's metadata (D1 enforcement). */
  frecuencia: PlanFrecuenciaEnum.optional(),
  responsableTexto: z.string().max(200).optional(),
  meses: MesesArraySchema.optional(),
  attrs: NodeAttrsSchema.optional(),
});
export type PlanNodeCreateInput = z.infer<typeof PlanNodeCreateSchema>;

export const PlanNodeUpdateSchema = z.object({
  nombre: z.string().min(1).max(200).optional(),
  orden: z.number().int().min(0).optional(),
  tabIndex: z.number().int().min(0).optional(),
  frecuencia: PlanFrecuenciaEnum.nullable().optional(),
  responsableTexto: z.string().max(200).nullable().optional(),
  meses: MesesArraySchema.nullable().optional(),
  attrs: NodeAttrsSchema.nullable().optional(),
});
export type PlanNodeUpdateInput = z.infer<typeof PlanNodeUpdateSchema>;

// =============================================================================
// Leaf notes (Schwager parity — notes per leaf per month)
// =============================================================================

export const PlanLeafNoteCreateSchema = z.object({
  mesIndex: z.number().int().min(0).max(52),
  texto: z.string().min(1).max(10000),
});
export type PlanLeafNoteCreateInput = z.infer<typeof PlanLeafNoteCreateSchema>;

export const PlanLeafNoteUpdateSchema = z.object({
  texto: z.string().min(1).max(10000),
});
export type PlanLeafNoteUpdateInput = z.infer<typeof PlanLeafNoteUpdateSchema>;

export interface PlanLeafNoteDto {
  id: number;
  nodeId: number;
  mesIndex: number;
  texto: string;
  createdById: number;
  createdByNombre: string | null;
  createdAt: string;
  updatedAt: string;
}

export const PlanNodeReorderSchema = z.object({
  tabIndex: z.number().int().min(0),
  ordering: z
    .array(
      z.object({
        id: z.number().int().positive(),
        orden: z.number().int().min(0),
      }),
    )
    .min(1)
    .max(500),
});
export type PlanNodeReorderInput = z.infer<typeof PlanNodeReorderSchema>;

// =============================================================================
// Assignment schemas
// =============================================================================

export const PlanAssignmentCreateSchema = z.object({
  usuarioId: z.number().int().positive(),
});
export type PlanAssignmentCreateInput = z.infer<typeof PlanAssignmentCreateSchema>;

// =============================================================================
// Evidencia presigned upload schemas
// =============================================================================

const ALLOWED_EVIDENCIA_MIME = /^(application\/pdf|image\/(jpeg|png|webp|gif)|video\/(mp4|webm|quicktime)|audio\/(mpeg|wav|webm|ogg))$/;

export const PlanEvidenciaPresignSchema = z.object({
  nodeId: z.number().int().positive(),
  mesIndex: z.number().int().min(0).max(52),
  tipo: PlanEvidenciaTypeEnum,
  filename: z.string().min(1).max(255),
  mimeType: z
    .string()
    .regex(
      ALLOWED_EVIDENCIA_MIME,
      'Solo PDF, JPG, PNG, WEBP, GIF, MP4, WEBM, MOV, MP3, WAV, OGG',
    ),
  fileSize: z
    .number()
    .int()
    .positive()
    .max(50 * 1024 * 1024, 'Max 50 MB por archivo'),
});
export type PlanEvidenciaPresignInput = z.infer<typeof PlanEvidenciaPresignSchema>;

export const PlanEvidenciaConfirmSchema = z.object({
  nodeId: z.number().int().positive(),
  mesIndex: z.number().int().min(0).max(52),
  tipo: PlanEvidenciaTypeEnum,
  filename: z.string().min(1).max(255),
  /** Must start with `planes/{empresaId}/` — service re-validates against actual empresaId. */
  s3Key: z
    .string()
    .regex(
      /^planes\/\d+\/\d+\/\d+\/\d+\/\d+-/,
      'Invalid s3Key format (expected planes/{empresaId}/{planId}/{nodeId}/{mesIndex}/{ts}-{name})',
    ),
  mimeType: z.string().regex(ALLOWED_EVIDENCIA_MIME),
  fileSize: z.number().int().positive().max(50 * 1024 * 1024),
  notas: z.string().max(1000).optional(),
});
export type PlanEvidenciaConfirmInput = z.infer<typeof PlanEvidenciaConfirmSchema>;

// =============================================================================
// Excel import schemas
// =============================================================================

export const PlanExcelCreateFromBodySchema = z.object({
  codigo: z.string().min(1).max(50),
  nombre: z.string().min(1).max(200).optional(),
  categoria: PlanCategoriaEnum.optional(),
});
export type PlanExcelCreateFromBody = z.infer<typeof PlanExcelCreateFromBodySchema>;

// =============================================================================
// Domain types (mirror Prisma models but kept transport-safe)
// =============================================================================

export interface PlanNodeAssignmentDto {
  id: number;
  nodeId: number;
  usuarioId: number;
  nombreSnapshot: string;
  rutSnapshot: string;
  cargoSnapshot: string | null;
  assignedById: number;
  createdAt: string;
}

export interface PlanEvidenciaDto {
  id: number;
  nodeId: number;
  mesIndex: number;
  tipo: PlanEvidenciaType;
  filename: string;
  s3Key: string;
  mimeType: string;
  fileSize: number;
  notas: string | null;
  uploadedById: number;
  createdAt: string;
}

export interface PlanNodeDto {
  id: number;
  planId: number;
  parentId: number | null;
  nombre: string;
  orden: number;
  tabIndex: number;
  frecuencia: PlanFrecuencia | null;
  responsableTexto: string | null;
  meses: boolean[] | null;
  attrs: Record<string, string> | null;
  createdAt: string;
  updatedAt: string;
  children: PlanNodeDto[];
  assignments: PlanNodeAssignmentDto[];
  evidencias: PlanEvidenciaDto[];
}

export interface PlanDto {
  id: number;
  empresaId: number;
  codigo: string;
  nombre: string;
  categoria: PlanCategoria;
  version: string;
  contrato: string | null;
  objetivoGeneral: string | null;
  alcance: string | null;
  keywords: string[];
  anio: number;
  mesInicio: number;
  periodType: PlanPeriodType;
  numPeriods: number;
  mesesLabels: string[];
  customDates: string[] | null;
  columnLabels: string[] | null;
  columnWidths: number[] | null;
  columnConfig: PlanColumnConfig | null;
  campos: Record<string, boolean>;
  tabLabels: string[];
  status: PlanStatus;
  createdById: number;
  createdAt: string;
  updatedAt: string;
  nodes: PlanNodeDto[];
}

export interface PlanListItemDto {
  id: number;
  codigo: string;
  nombre: string;
  categoria: PlanCategoria;
  anio: number;
  status: PlanStatus;
  createdById: number;
  createdAt: string;
  updatedAt: string;
}

// =============================================================================
// Domain helpers (pure functions, shared frontend + backend)
// =============================================================================

/** A node is a "leaf" (= actividad) when it has zero children. */
export function isLeafNode(node: Pick<PlanNodeDto, 'children'>): boolean {
  return node.children.length === 0;
}

/**
 * Compute UI status for a leaf node at a given month.
 * - NO_APLICA: meses[i] explicitly false (activity not scheduled this period)
 * - EJECUTADO: at least one evidencia uploaded for this mesIndex
 * - PENDIENTE: scheduled but no evidencia yet
 *
 * Branch nodes have no meses array — caller should not invoke this on a branch.
 */
export function computeActividadStatus(
  node: Pick<PlanNodeDto, 'meses' | 'evidencias'>,
  mesIndex: number,
): PlanActivityStatus {
  if (node.meses && node.meses[mesIndex] === false) {
    return 'NO_APLICA';
  }
  const hasEvidencia = node.evidencias.some((e) => e.mesIndex === mesIndex);
  return hasEvidencia ? 'EJECUTADO' : 'PENDIENTE';
}

/**
 * Smart-apply: convert a frecuencia + numPeriods into a meses boolean array.
 * Used by the UI when the user picks "MENSUAL", "BIMESTRAL", etc. to auto-fill
 * the meses checkboxes. Returns null for `CADA_VEZ`/`UNICA`/`N_A`/`OTRO`
 * (caller should keep manual toggle behavior).
 */
export function frecuenciaToMeses(
  frecuencia: PlanFrecuencia,
  numPeriods: number,
): boolean[] | null {
  const arr = (everyN: number): boolean[] =>
    Array.from({ length: numPeriods }, (_, i) => i % everyN === 0);

  switch (frecuencia) {
    case 'MENSUAL':
      return Array.from({ length: numPeriods }, () => true);
    case 'BIMESTRAL':
      return arr(2);
    case 'TRIMESTRAL':
      return arr(3);
    case 'CUATRIMESTRAL':
      return arr(4);
    case 'SEMESTRAL':
      return arr(6);
    case 'ANUAL':
      return Array.from({ length: numPeriods }, (_, i) => i === 0);
    case 'CADA_VEZ':
    case 'UNICA':
    case 'N_A':
    case 'OTRO':
      return null;
  }
}
